"use client"

import * as React from 'react'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { useOrganizationScopeVersion, useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'

type TrainPlanData = {
  id: string
  plan_number: string
  name: string | null
  status: string
  locomotive_id: string
  total_weight_tonnes: number | null
  total_length_metres: number | null
  departure_date: string | null
  validation_errors: string[] | null
}

type WagonSlotRow = {
  id: string
  wagon_id: string
  position: number
  cargo_reference: string | null
  load_tonnes: number | null
  notes: string | null
}

type ValidationResult = {
  valid: boolean
  errors: string[]
  totals: { weightTonnes: number; lengthMetres: number }
}

type PlanResponse = {
  items: Array<Record<string, unknown>>
  total: number
}

type WagonsResponse = {
  items: Array<Record<string, unknown>>
  total: number
}

function mapPlanData(item: Record<string, unknown>): TrainPlanData | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    plan_number: typeof item.plan_number === 'string' ? item.plan_number : '',
    name: typeof item.name === 'string' ? item.name : null,
    status: typeof item.status === 'string' ? item.status : 'draft',
    locomotive_id: typeof item.locomotive_id === 'string' ? item.locomotive_id : '',
    total_weight_tonnes: typeof item.total_weight_tonnes === 'number' ? item.total_weight_tonnes : null,
    total_length_metres: typeof item.total_length_metres === 'number' ? item.total_length_metres : null,
    departure_date: typeof item.departure_date === 'string' ? item.departure_date : null,
    validation_errors: Array.isArray(item.validation_errors) ? item.validation_errors as string[] : null,
  }
}

function mapWagonSlotRow(item: Record<string, unknown>): WagonSlotRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    wagon_id: typeof item.wagon_id === 'string' ? item.wagon_id : '',
    position: typeof item.position === 'number' ? item.position : 0,
    cargo_reference: typeof item.cargo_reference === 'string' ? item.cargo_reference : null,
    load_tonnes: typeof item.load_tonnes === 'number' ? item.load_tonnes : null,
    notes: typeof item.notes === 'string' ? item.notes : null,
  }
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  validated: 'Validated',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
}

const STATUS_CLASS: Record<string, string> = {
  draft: 'text-muted-foreground',
  validated: 'text-blue-600',
  confirmed: 'text-green-600',
  cancelled: 'text-red-600',
}

export default function TrainBuilderPage({ params }: { params?: { id?: string } }) {
  const id = typeof params?.id === 'string' ? params.id : ''
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { tenantId } = useOrganizationScopeDetail()
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'train-planning-detail',
  })

  const [plan, setPlan] = React.useState<TrainPlanData | null>(null)
  const [wagons, setWagons] = React.useState<WagonSlotRow[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [wagonsLoading, setWagonsLoading] = React.useState(false)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [showAddForm, setShowAddForm] = React.useState(false)
  const [newWagonId, setNewWagonId] = React.useState('')
  const [newCargoRef, setNewCargoRef] = React.useState('')
  const [newLoadTonnes, setNewLoadTonnes] = React.useState('')
  const [validating, setValidating] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)
  const [validationResult, setValidationResult] = React.useState<ValidationResult | null>(null)
  const [reloadToken, setReloadToken] = React.useState(0)

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  React.useEffect(() => {
    if (!id) {
      setPlan(null)
      setIsLoading(false)
      setLoadError(t('train_planning.builder.error.notFound', 'Train plan not found'))
      return
    }
    let cancelled = false

    async function loadPlan() {
      setIsLoading(true)
      setLoadError(null)
      try {
        const fallback: PlanResponse = { items: [], total: 0 }
        const call = await apiCall<PlanResponse>(
          `/api/train_planning/train-plans?ids=${encodeURIComponent(id)}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('train_planning.builder.error.load', 'Failed to load train plan')
          if (!cancelled) setLoadError(message)
          return
        }
        const payload = call.result ?? fallback
        const items = Array.isArray(payload.items) ? payload.items : []
        const first = items[0]
        if (!cancelled) {
          setPlan(first ? mapPlanData(first) : null)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('train_planning.builder.error.load', 'Failed to load train plan')
          setLoadError(message)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadPlan()
    return () => { cancelled = true }
  }, [id, reloadToken, scopeVersion, t])

  React.useEffect(() => {
    if (!id) {
      setWagons([])
      setWagonsLoading(false)
      return
    }
    let cancelled = false

    async function loadWagons() {
      setWagonsLoading(true)
      try {
        const fallback: WagonsResponse = { items: [], total: 0 }
        const call = await apiCall<WagonsResponse>(
          `/api/train_planning/train-plans/wagons?trainPlanId=${encodeURIComponent(id)}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('train_planning.builder.error.loadWagons', 'Failed to load wagon consist')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setWagons(items.map(mapWagonSlotRow).filter((row): row is WagonSlotRow => row !== null))
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('train_planning.builder.error.loadWagons', 'Failed to load wagon consist')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setWagonsLoading(false)
      }
    }

    loadWagons()
    return () => { cancelled = true }
  }, [id, reloadToken, scopeVersion, t])

  const handleValidate = React.useCallback(async () => {
    if (!id) return
    setValidating(true)
    setValidationResult(null)
    try {
      const call = await runMutation({
        operation: () => apiCallOrThrow<ValidationResult>(
          '/api/train_planning/train-plans/validate',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id }),
          },
          { errorMessage: t('train_planning.builder.error.validate', 'Validation failed') },
        ),
        context: {},
      })
      setValidationResult(call.result)
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('train_planning.builder.error.validate', 'Validation failed')
      flash(message, 'error')
    } finally {
      setValidating(false)
    }
  }, [id, handleRefresh, t])

  const handleConfirm = React.useCallback(async () => {
    if (!id || !plan) return
    const confirmed = await confirm({
      title: t('train_planning.builder.confirmPlan', 'Confirm plan {{planNumber}}?', { planNumber: plan.plan_number }),
      variant: 'default',
    })
    if (!confirmed) return
    setConfirming(true)
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          '/api/train_planning/train-plans/confirm',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id }),
          },
          { errorMessage: t('train_planning.builder.error.confirm', 'Failed to confirm plan') },
        ),
        context: {},
      })
      flash(t('train_planning.builder.flash.confirmed', 'Train plan confirmed'), 'success')
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('train_planning.builder.error.confirm', 'Failed to confirm plan')
      flash(message, 'error')
    } finally {
      setConfirming(false)
    }
  }, [id, plan, confirm, handleRefresh, t])

  const handleAddWagon = React.useCallback(async () => {
    if (!id || !newWagonId.trim()) {
      flash(t('train_planning.builder.error.wagonIdRequired', 'Wagon ID is required'), 'error')
      return
    }
    const nextPosition = wagons.length > 0 ? Math.max(...wagons.map((w) => w.position)) + 1 : 1
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          '/api/train_planning/train-plans/wagons',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              trainPlanId: id,
              wagonId: newWagonId.trim(),
              cargoReference: newCargoRef.trim() || null,
              loadTonnes: newLoadTonnes ? parseFloat(newLoadTonnes) : null,
              tenantId,
              position: nextPosition,
            }),
          },
          { errorMessage: t('train_planning.builder.error.addWagon', 'Failed to add wagon to consist') },
        ),
        context: {},
      })
      flash(t('train_planning.builder.flash.wagonAdded', 'Wagon added to consist'), 'success')
      setNewWagonId('')
      setNewCargoRef('')
      setNewLoadTonnes('')
      setShowAddForm(false)
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('train_planning.builder.error.addWagon', 'Failed to add wagon to consist')
      flash(message, 'error')
    }
  }, [id, newWagonId, newCargoRef, newLoadTonnes, wagons, tenantId, handleRefresh, t])

  const handleRemoveWagon = React.useCallback(async (slotId: string) => {
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          '/api/train_planning/train-plans/wagons',
          {
            method: 'DELETE',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id: slotId }),
          },
          { errorMessage: t('train_planning.builder.error.removeWagon', 'Failed to remove wagon from consist') },
        ),
        context: {},
      })
      setWagons((prev) => prev.filter((w) => w.id !== slotId))
      flash(t('train_planning.builder.flash.wagonRemoved', 'Wagon removed from consist'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('train_planning.builder.error.removeWagon', 'Failed to remove wagon from consist')
      flash(message, 'error')
    }
  }, [runMutation, t])

  const wagonColumns = React.useMemo<ColumnDef<WagonSlotRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'position',
        header: t('train_planning.builder.columns.position', '#'),
        cell: ({ row }) => (
          <span className="font-mono font-medium">{row.original.position}</span>
        ),
      },
      {
        accessorKey: 'wagon_id',
        header: t('train_planning.builder.columns.wagonId', 'Wagon ID'),
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.wagon_id.slice(0, 8)}…
          </span>
        ),
      },
      {
        accessorKey: 'cargo_reference',
        header: t('train_planning.builder.columns.cargoReference', 'Cargo Ref'),
        cell: ({ row }) => row.original.cargo_reference ?? noValue,
      },
      {
        accessorKey: 'load_tonnes',
        header: t('train_planning.builder.columns.loadTonnes', 'Load (t)'),
        cell: ({ row }) => row.original.load_tonnes !== null ? `${row.original.load_tonnes}` : noValue,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <RowActions
            items={[
              {
                id: 'remove',
                label: t('train_planning.builder.actions.removeWagon', 'Remove'),
                destructive: true,
                onSelect: () => { void handleRemoveWagon(row.original.id) },
              },
            ]}
          />
        ),
      },
    ]
  }, [t, handleRemoveWagon])

  return (
    <Page>
      <PageBody>
        {isLoading && <LoadingMessage label="" />}
        {loadError && <ErrorMessage label={loadError} />}
        {!isLoading && !loadError && !plan && (
          <ErrorMessage label={t('train_planning.builder.error.notFound', 'Train plan not found')} />
        )}
        {!isLoading && !loadError && plan && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-mono text-xl font-bold">{plan.plan_number}</h2>
                {plan.name && (
                  <p className="text-muted-foreground">{plan.name}</p>
                )}
                <span className={STATUS_CLASS[plan.status] ?? ''}>
                  {STATUS_LABELS[plan.status] ?? plan.status}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { void handleValidate() }}
                  disabled={validating}
                >
                  {validating
                    ? t('train_planning.builder.validating', 'Validating...')
                    : t('train_planning.builder.actions.validate', 'Validate Consist')}
                </Button>
                <Button
                  type="button"
                  onClick={() => { void handleConfirm() }}
                  disabled={confirming || plan.status !== 'validated'}
                >
                  {confirming
                    ? t('train_planning.builder.confirming', 'Confirming...')
                    : t('train_planning.builder.actions.confirm', 'Confirm Plan')}
                </Button>
              </div>
            </div>

            {validationResult && (
              <div className="mb-6 rounded border p-4">
                <h3 className="mb-2 font-semibold">
                  {t('train_planning.builder.validationResults', 'Validation Results')}
                </h3>
                {validationResult.valid && (
                  <p className="text-green-600">
                    {t('train_planning.builder.consistValid', 'Consist is valid')}
                  </p>
                )}
                {validationResult.errors.map((error, index) => (
                  <p key={index} className="text-red-600">{error}</p>
                ))}
                <div className="mt-2 flex gap-4 text-sm text-muted-foreground">
                  <span>
                    {t('train_planning.builder.totalWeight', 'Weight: {{weight}} t', { weight: validationResult.totals.weightTonnes })}
                  </span>
                  <span>
                    {t('train_planning.builder.totalLength', 'Length: {{length}} m', { length: validationResult.totals.lengthMetres })}
                  </span>
                </div>
              </div>
            )}

            <DataTable<WagonSlotRow>
              title={t('train_planning.builder.consistTitle', 'Wagon Consist')}
              refreshButton={{
                label: t('train_planning.builder.actions.refresh', 'Refresh'),
                onRefresh: handleRefresh,
              }}
              actions={(
                <Button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                >
                  {t('train_planning.builder.actions.addWagon', 'Add Wagon')}
                </Button>
              )}
              columns={wagonColumns}
              data={wagons}
              isLoading={wagonsLoading}
            />

            {showAddForm && (
              <div className="mt-4 rounded border p-4">
                <h3 className="mb-3 font-semibold">
                  {t('train_planning.builder.addWagonForm.title', 'Add Wagon to Consist')}
                </h3>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      {t('train_planning.builder.addWagonForm.wagonId', 'Wagon ID (UUID)')}
                      <span className="ml-1 text-red-500">*</span>
                    </label>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                      value={newWagonId}
                      onChange={(e) => setNewWagonId(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      {t('train_planning.builder.addWagonForm.cargoReference', 'Cargo Reference')}
                    </label>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      value={newCargoRef}
                      onChange={(e) => setNewCargoRef(e.target.value)}
                      placeholder={t('train_planning.builder.addWagonForm.cargoReferencePlaceholder', 'e.g. CONT-2026-00123')}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      {t('train_planning.builder.addWagonForm.loadTonnes', 'Load (tonnes)')}
                    </label>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      type="number"
                      min="0"
                      step="0.1"
                      value={newLoadTonnes}
                      onChange={(e) => setNewLoadTonnes(e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" onClick={() => { void handleAddWagon() }}>
                      {t('train_planning.builder.addWagonForm.submit', 'Add to Consist')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAddForm(false)
                        setNewWagonId('')
                        setNewCargoRef('')
                        setNewLoadTonnes('')
                      }}
                    >
                      {t('train_planning.builder.addWagonForm.cancel', 'Cancel')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
